/**
 * V55.22: 좌석배치도 속성 패널 컴포넌트
 *
 * Zone, Seat, Meeting Room, Floor, TextBox의 속성을 편집하는 패널입니다.
 * - Zone: zoneCode, label, 크기(+/-) 편집
 * - Seat: zoneId (드롭다운), seatNo 편집
 * - Meeting Room: label, 크기(+/-) 편집
 * - Floor: text, fontSize, textAlign(X/Y), 크기(+/-) 편집
 * - TextBox: text, fontSize, textColor, 크기(+/-) 편집
 *
 * V55.22 변경사항:
 * - Floor 타입 속성 패널 추가 (텍스트 정렬 지원)
 * - TextBox 타입 속성 패널 추가 (색상 팔레트 지원)
 *
 * V54.7 변경사항:
 * - Meeting Room 지원 추가 (이름/크기 조절)
 * - ResizeObserver loop 방지: +/- 버튼에 rAF 적용
 *
 * @module pages/organization/components/seatMap/SeatMapPropertiesPanel
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  X,
  LayoutGrid,
  Hash,
  Tag,
  Minus,
  Plus,
  Move,
  Users,
  Layers,           // V55.22: Floor 아이콘
  Type,             // V55.22: TextBox 아이콘
  AlignLeft,        // V55.22: 텍스트 정렬
  AlignCenter,
  AlignRight,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
} from 'lucide-react';

import {
  SeatMapObject,
  formatSeatCode,
  TEXT_COLOR_PALETTE, // V55.22
  TextAlignX,         // V55.22
  TextAlignY,         // V55.22
} from '../../../../shared/types/seatMap';

// ============================================
// Props 타입
// ============================================

// V52.4: Zone 크기 조절 상수
const ZONE_SIZE_STEP = 20;
const ZONE_MIN_WIDTH = 200;
const ZONE_MIN_HEIGHT = 150;

// V54.7: Meeting Room 크기 조절 상수
const MEETING_ROOM_SIZE_STEP = 20;
const MEETING_ROOM_MIN_WIDTH = 100;
const MEETING_ROOM_MIN_HEIGHT = 80;

// V55.22: Floor 크기 조절 상수
const FLOOR_SIZE_STEP = 50;
const FLOOR_MIN_WIDTH = 200;
const FLOOR_MIN_HEIGHT = 150;

// V55.22: TextBox 크기 조절 상수
const TEXTBOX_SIZE_STEP = 20;
const TEXTBOX_MIN_WIDTH = 80;
const TEXTBOX_MIN_HEIGHT = 40;

// V55.22: 폰트 크기 범위
const FONT_SIZE_MIN = 10;
const FONT_SIZE_MAX = 72;
const FONT_SIZE_STEP = 2;

interface SeatMapPropertiesPanelProps {
  /** 선택된 오브젝트 */
  selectedObject: SeatMapObject;

  /** Zone 목록 (좌석의 zone 선택용) */
  zones: SeatMapObject[];

  /** 속성 변경 핸들러 */
  onUpdateObject: (objectId: string, updates: Partial<SeatMapObject>) => void;

  /** 패널 닫기 핸들러 */
  onClose: () => void;

  /** V52.3: 중복 좌석 코드 Set (경고 표시용) */
  duplicateSeatCodes?: Set<string>;
}

// ============================================
// 패널 컴포넌트
// ============================================

export function SeatMapPropertiesPanel({
  selectedObject,
  zones,
  onUpdateObject,
  onClose,
  duplicateSeatCodes,
}: SeatMapPropertiesPanelProps) {
  const {
    id,
    type,
    label,
    zoneCode,
    zoneId,
    seatNo,
    // V55.22: floor/textBox 관련 필드
    text,
    fontSize = 16,
    textColor = '#111827',
    alignX = 'center' as TextAlignX,
    alignY = 'middle' as TextAlignY,
  } = selectedObject;

  // V54.7: ResizeObserver loop 방지를 위한 rAF ref
  const rafRef = useRef<number | null>(null);

  // 로컬 상태 (입력 중 변경)
  const [localLabel, setLocalLabel] = useState(label || '');
  const [localZoneCode, setLocalZoneCode] = useState(zoneCode || '');
  const [localZoneId, setLocalZoneId] = useState(zoneId || '');
  const [localSeatNo, setLocalSeatNo] = useState(seatNo || '');
  // V55.22: floor/textBox 로컬 상태
  const [localText, setLocalText] = useState(text || '');
  const [localFontSize, setLocalFontSize] = useState(fontSize);
  const [localTextColor, setLocalTextColor] = useState(textColor);
  const [localAlignX, setLocalAlignX] = useState<TextAlignX>(alignX);
  const [localAlignY, setLocalAlignY] = useState<TextAlignY>(alignY);

  // 선택 오브젝트 변경 시 로컬 상태 동기화
  useEffect(() => {
    setLocalLabel(label || '');
    setLocalZoneCode(zoneCode || '');
    setLocalZoneId(zoneId || '');
    setLocalSeatNo(seatNo || '');
    // V55.22: floor/textBox 상태 동기화
    setLocalText(text || '');
    setLocalFontSize(fontSize);
    setLocalTextColor(textColor);
    setLocalAlignX(alignX);
    setLocalAlignY(alignY);
  }, [id, label, zoneCode, zoneId, seatNo, text, fontSize, textColor, alignX, alignY]);

  // V54.7: cleanup rAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // V52.3: 현재 좌석 코드가 중복인지 확인
  const isDuplicate = type === 'seat' && duplicateSeatCodes && (() => {
    const zone = zones.find((z) => z.id === localZoneId);
    const zCode = zone?.zoneCode;
    const currentSeatCode = formatSeatCode(zCode, localSeatNo);
    return currentSeatCode && duplicateSeatCodes.has(currentSeatCode);
  })();

  // 변경 적용 핸들러
  const handleApply = useCallback(() => {
    if (type === 'zone') {
      onUpdateObject(id, {
        label: localLabel || undefined,
        zoneCode: localZoneCode || undefined,
      });
    } else if (type === 'seat') {
      onUpdateObject(id, {
        zoneId: localZoneId || undefined,
        seatNo: localSeatNo || undefined,
      });
    } else if (type === 'meeting_room') {
      // V54.7: Meeting Room 라벨 변경
      onUpdateObject(id, {
        label: localLabel || undefined,
      });
    } else if (type === 'floor') {
      // V55.22: Floor 텍스트 및 정렬 변경
      onUpdateObject(id, {
        text: localText || undefined,
        fontSize: localFontSize,
        alignX: localAlignX,
        alignY: localAlignY,
      });
    } else if (type === 'textBox') {
      // V55.22: TextBox 텍스트, 폰트, 색상 변경
      onUpdateObject(id, {
        text: localText || undefined,
        fontSize: localFontSize,
        textColor: localTextColor,
      });
    }
  }, [type, id, localLabel, localZoneCode, localZoneId, localSeatNo, localText, localFontSize, localTextColor, localAlignX, localAlignY, onUpdateObject]);

  // 입력 필드 blur 시 자동 적용
  const handleBlur = useCallback(() => {
    handleApply();
  }, [handleApply]);

  // 엔터 키 입력 시 적용
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleApply();
      }
    },
    [handleApply]
  );

  // V54.7: ResizeObserver loop 방지를 위한 rAF 래퍼
  // 크기 변경을 다음 프레임으로 지연하여 resize observer 콜백과 동기 실행 방지
  const scheduleUpdate = useCallback(
    (updates: Partial<SeatMapObject>) => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        onUpdateObject(id, updates);
      });
    },
    [id, onUpdateObject]
  );

  // V52.4: Zone 크기 조절 핸들러 (V54.7: rAF 적용)
  const handleZoneWidthChange = useCallback(
    (delta: number) => {
      const newWidth = Math.max(ZONE_MIN_WIDTH, selectedObject.width + delta);
      scheduleUpdate({ width: newWidth });
    },
    [selectedObject.width, scheduleUpdate]
  );

  const handleZoneHeightChange = useCallback(
    (delta: number) => {
      const newHeight = Math.max(ZONE_MIN_HEIGHT, selectedObject.height + delta);
      scheduleUpdate({ height: newHeight });
    },
    [selectedObject.height, scheduleUpdate]
  );

  // V54.7: Meeting Room 크기 조절 핸들러
  const handleMeetingRoomWidthChange = useCallback(
    (delta: number) => {
      const newWidth = Math.max(MEETING_ROOM_MIN_WIDTH, selectedObject.width + delta);
      scheduleUpdate({ width: newWidth });
    },
    [selectedObject.width, scheduleUpdate]
  );

  const handleMeetingRoomHeightChange = useCallback(
    (delta: number) => {
      const newHeight = Math.max(MEETING_ROOM_MIN_HEIGHT, selectedObject.height + delta);
      scheduleUpdate({ height: newHeight });
    },
    [selectedObject.height, scheduleUpdate]
  );

  // V55.22: Floor 크기 조절 핸들러
  const handleFloorWidthChange = useCallback(
    (delta: number) => {
      const newWidth = Math.max(FLOOR_MIN_WIDTH, selectedObject.width + delta);
      scheduleUpdate({ width: newWidth });
    },
    [selectedObject.width, scheduleUpdate]
  );

  const handleFloorHeightChange = useCallback(
    (delta: number) => {
      const newHeight = Math.max(FLOOR_MIN_HEIGHT, selectedObject.height + delta);
      scheduleUpdate({ height: newHeight });
    },
    [selectedObject.height, scheduleUpdate]
  );

  // V55.22: TextBox 크기 조절 핸들러
  const handleTextBoxWidthChange = useCallback(
    (delta: number) => {
      const newWidth = Math.max(TEXTBOX_MIN_WIDTH, selectedObject.width + delta);
      scheduleUpdate({ width: newWidth });
    },
    [selectedObject.width, scheduleUpdate]
  );

  const handleTextBoxHeightChange = useCallback(
    (delta: number) => {
      const newHeight = Math.max(TEXTBOX_MIN_HEIGHT, selectedObject.height + delta);
      scheduleUpdate({ height: newHeight });
    },
    [selectedObject.height, scheduleUpdate]
  );

  // V55.22: 폰트 크기 조절 핸들러
  const handleFontSizeChange = useCallback(
    (delta: number) => {
      const newSize = Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, localFontSize + delta));
      setLocalFontSize(newSize);
      scheduleUpdate({ fontSize: newSize });
    },
    [localFontSize, scheduleUpdate]
  );

  // V55.22: 텍스트 색상 변경 핸들러
  const handleTextColorChange = useCallback(
    (color: string) => {
      setLocalTextColor(color);
      scheduleUpdate({ textColor: color });
    },
    [scheduleUpdate]
  );

  // V55.22: 텍스트 정렬 변경 핸들러
  const handleAlignXChange = useCallback(
    (align: TextAlignX) => {
      setLocalAlignX(align);
      scheduleUpdate({ alignX: align });
    },
    [scheduleUpdate]
  );

  const handleAlignYChange = useCallback(
    (align: TextAlignY) => {
      setLocalAlignY(align);
      scheduleUpdate({ alignY: align });
    },
    [scheduleUpdate]
  );

  // V55.22: 패널 헤더 타이틀
  const getPanelTitle = () => {
    switch (type) {
      case 'zone':
        return '구역 속성';
      case 'seat':
        return '좌석 속성';
      case 'meeting_room':
        return '회의실 속성';
      case 'floor':
        return '바닥 속성';
      case 'textBox':
        return '텍스트 박스 속성';
      default:
        return '속성';
    }
  };

  // V55.22: 패널 헤더 아이콘
  const getPanelIcon = () => {
    switch (type) {
      case 'meeting_room':
        return <Users size={18} color="#22c55e" />;
      case 'floor':
        return <Layers size={18} color="#64748b" />;
      case 'textBox':
        return <Type size={18} color="#3b82f6" />;
      default:
        return <LayoutGrid size={18} color="#6366f1" />;
    }
  };

  // V54.9: 패널 내부 클릭 이벤트가 캔버스로 전파되지 않도록 차단
  const handlePanelMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      onMouseDown={handlePanelMouseDown}
      style={{
        width: '280px',
        background: 'white',
        borderLeft: '1px solid var(--color-gray-200)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* 헤더 */}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid var(--color-gray-200)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {getPanelIcon()}
          <span
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1e293b',
            }}
          >
            {getPanelTitle()}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            padding: '4px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderRadius: '4px',
          }}
        >
          <X size={18} color="#64748b" />
        </button>
      </div>

      {/* 속성 입력 영역 */}
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Zone 속성 */}
        {type === 'zone' && (
          <>
            {/* 구역 코드 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Hash size={14} />
                구역 코드
              </label>
              <input
                type="text"
                value={localZoneCode}
                onChange={(e) => setLocalZoneCode(e.target.value.toUpperCase())}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder="예: A, B, C"
                maxLength={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  textTransform: 'uppercase',
                }}
              />
              <div
                style={{
                  marginTop: '4px',
                  fontSize: '11px',
                  color: '#94a3b8',
                }}
              >
                좌석 표시에 사용됩니다 (예: A-1, B-2)
              </div>
            </div>

            {/* 라벨 (구역명) */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Tag size={14} />
                구역명
              </label>
              <input
                type="text"
                value={localLabel}
                onChange={(e) => setLocalLabel(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder="예: 개발팀, 영업팀"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>

            {/* V52.4: 구역 크기 조절 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Move size={14} />
                구역 크기
              </label>

              {/* 가로 크기 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', width: '32px' }}>가로</span>
                <button
                  onClick={() => handleZoneWidthChange(-ZONE_SIZE_STEP)}
                  disabled={selectedObject.width <= ZONE_MIN_WIDTH}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: selectedObject.width <= ZONE_MIN_WIDTH ? '#f1f5f9' : 'white',
                    cursor: selectedObject.width <= ZONE_MIN_WIDTH ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={14} color={selectedObject.width <= ZONE_MIN_WIDTH ? '#cbd5e1' : '#64748b'} />
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1e293b',
                  }}
                >
                  {selectedObject.width}px
                </span>
                <button
                  onClick={() => handleZoneWidthChange(ZONE_SIZE_STEP)}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={14} color="#64748b" />
                </button>
              </div>

              {/* 세로 크기 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', width: '32px' }}>세로</span>
                <button
                  onClick={() => handleZoneHeightChange(-ZONE_SIZE_STEP)}
                  disabled={selectedObject.height <= ZONE_MIN_HEIGHT}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: selectedObject.height <= ZONE_MIN_HEIGHT ? '#f1f5f9' : 'white',
                    cursor: selectedObject.height <= ZONE_MIN_HEIGHT ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={14} color={selectedObject.height <= ZONE_MIN_HEIGHT ? '#cbd5e1' : '#64748b'} />
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1e293b',
                  }}
                >
                  {selectedObject.height}px
                </span>
                <button
                  onClick={() => handleZoneHeightChange(ZONE_SIZE_STEP)}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={14} color="#64748b" />
                </button>
              </div>

              <div
                style={{
                  marginTop: '8px',
                  fontSize: '11px',
                  color: '#94a3b8',
                }}
              >
                최소: {ZONE_MIN_WIDTH}x{ZONE_MIN_HEIGHT}px
              </div>
            </div>
          </>
        )}

        {/* Seat 속성 */}
        {type === 'seat' && (
          <>
            {/* 소속 구역 선택 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <LayoutGrid size={14} />
                소속 구역
              </label>
              <select
                value={localZoneId}
                onChange={(e) => {
                  setLocalZoneId(e.target.value);
                  // 즉시 적용
                  onUpdateObject(id, { zoneId: e.target.value || undefined });
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                <option value="">없음</option>
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.zoneCode ? `${zone.zoneCode}구역` : zone.label || zone.id.slice(0, 8)}
                    {zone.label && zone.zoneCode ? ` (${zone.label})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* 좌석 번호 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Hash size={14} />
                좌석 번호
              </label>
              <input
                type="text"
                value={localSeatNo}
                onChange={(e) => setLocalSeatNo(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder="예: 1, 2, 12"
                maxLength={5}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
              <div
                style={{
                  marginTop: '4px',
                  fontSize: '11px',
                  color: '#94a3b8',
                }}
              >
                구역코드와 결합하여 표시됩니다 (예: A-1)
              </div>
            </div>

            {/* 미리보기 */}
            {(localZoneId || localSeatNo) && (
              <div
                style={{
                  padding: '12px',
                  background: isDuplicate ? '#fef2f2' : '#f1f5f9',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: isDuplicate ? '1px solid #fecaca' : 'none',
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    color: isDuplicate ? '#dc2626' : '#64748b',
                    marginBottom: '4px',
                  }}
                >
                  좌석 코드 미리보기
                </div>
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: isDuplicate ? '#dc2626' : '#1e40af',
                  }}
                >
                  {(() => {
                    const zone = zones.find((z) => z.id === localZoneId);
                    const zCode = zone?.zoneCode;
                    if (zCode && localSeatNo) return `${zCode}-${localSeatNo}`;
                    if (zCode) return zCode;
                    if (localSeatNo) return localSeatNo;
                    return '-';
                  })()}
                </div>
                {/* V52.3: 중복 경고 메시지 */}
                {isDuplicate && (
                  <div
                    style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#dc2626',
                      fontWeight: '500',
                    }}
                  >
                    ⚠️ 중복 좌석 코드입니다
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* V54.7: Meeting Room 속성 */}
        {type === 'meeting_room' && (
          <>
            {/* 회의실 이름 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Tag size={14} />
                회의실 이름
              </label>
              <input
                type="text"
                value={localLabel}
                onChange={(e) => setLocalLabel(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder="예: 대회의실, 소회의실A"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>

            {/* 회의실 크기 조절 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Move size={14} />
                회의실 크기
              </label>

              {/* 가로 크기 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', width: '32px' }}>가로</span>
                <button
                  onClick={() => handleMeetingRoomWidthChange(-MEETING_ROOM_SIZE_STEP)}
                  disabled={selectedObject.width <= MEETING_ROOM_MIN_WIDTH}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: selectedObject.width <= MEETING_ROOM_MIN_WIDTH ? '#f1f5f9' : 'white',
                    cursor: selectedObject.width <= MEETING_ROOM_MIN_WIDTH ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={14} color={selectedObject.width <= MEETING_ROOM_MIN_WIDTH ? '#cbd5e1' : '#64748b'} />
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1e293b',
                  }}
                >
                  {selectedObject.width}px
                </span>
                <button
                  onClick={() => handleMeetingRoomWidthChange(MEETING_ROOM_SIZE_STEP)}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={14} color="#64748b" />
                </button>
              </div>

              {/* 세로 크기 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', width: '32px' }}>세로</span>
                <button
                  onClick={() => handleMeetingRoomHeightChange(-MEETING_ROOM_SIZE_STEP)}
                  disabled={selectedObject.height <= MEETING_ROOM_MIN_HEIGHT}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: selectedObject.height <= MEETING_ROOM_MIN_HEIGHT ? '#f1f5f9' : 'white',
                    cursor: selectedObject.height <= MEETING_ROOM_MIN_HEIGHT ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={14} color={selectedObject.height <= MEETING_ROOM_MIN_HEIGHT ? '#cbd5e1' : '#64748b'} />
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1e293b',
                  }}
                >
                  {selectedObject.height}px
                </span>
                <button
                  onClick={() => handleMeetingRoomHeightChange(MEETING_ROOM_SIZE_STEP)}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={14} color="#64748b" />
                </button>
              </div>

              <div
                style={{
                  marginTop: '8px',
                  fontSize: '11px',
                  color: '#94a3b8',
                }}
              >
                최소: {MEETING_ROOM_MIN_WIDTH}x{MEETING_ROOM_MIN_HEIGHT}px
              </div>
            </div>
          </>
        )}

        {/* V55.22: Floor 속성 */}
        {type === 'floor' && (
          <>
            {/* 텍스트 입력 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Type size={14} />
                텍스트
              </label>
              <textarea
                value={localText}
                onChange={(e) => setLocalText(e.target.value)}
                onBlur={handleBlur}
                placeholder="바닥 영역 설명..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {/* 폰트 크기 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Tag size={14} />
                글꼴 크기
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => handleFontSizeChange(-FONT_SIZE_STEP)}
                  disabled={localFontSize <= FONT_SIZE_MIN}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: localFontSize <= FONT_SIZE_MIN ? '#f1f5f9' : 'white',
                    cursor: localFontSize <= FONT_SIZE_MIN ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={14} color={localFontSize <= FONT_SIZE_MIN ? '#cbd5e1' : '#64748b'} />
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1e293b',
                  }}
                >
                  {localFontSize}px
                </span>
                <button
                  onClick={() => handleFontSizeChange(FONT_SIZE_STEP)}
                  disabled={localFontSize >= FONT_SIZE_MAX}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: localFontSize >= FONT_SIZE_MAX ? '#f1f5f9' : 'white',
                    cursor: localFontSize >= FONT_SIZE_MAX ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={14} color={localFontSize >= FONT_SIZE_MAX ? '#cbd5e1' : '#64748b'} />
                </button>
              </div>
            </div>

            {/* 가로 정렬 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <AlignCenter size={14} />
                가로 정렬
              </label>
              <div style={{ display: 'flex', gap: '4px' }}>
                {(['left', 'center', 'right'] as const).map((align) => (
                  <button
                    key={align}
                    onClick={() => handleAlignXChange(align)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: localAlignX === align ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                      borderRadius: '6px',
                      background: localAlignX === align ? '#eff6ff' : 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={align === 'left' ? '왼쪽' : align === 'center' ? '가운데' : '오른쪽'}
                  >
                    {align === 'left' && <AlignLeft size={16} color={localAlignX === align ? '#3b82f6' : '#64748b'} />}
                    {align === 'center' && <AlignCenter size={16} color={localAlignX === align ? '#3b82f6' : '#64748b'} />}
                    {align === 'right' && <AlignRight size={16} color={localAlignX === align ? '#3b82f6' : '#64748b'} />}
                  </button>
                ))}
              </div>
            </div>

            {/* 세로 정렬 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <AlignCenterVertical size={14} />
                세로 정렬
              </label>
              <div style={{ display: 'flex', gap: '4px' }}>
                {(['top', 'middle', 'bottom'] as const).map((align) => (
                  <button
                    key={align}
                    onClick={() => handleAlignYChange(align)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: localAlignY === align ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                      borderRadius: '6px',
                      background: localAlignY === align ? '#eff6ff' : 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={align === 'top' ? '위' : align === 'middle' ? '중간' : '아래'}
                  >
                    {align === 'top' && <AlignStartVertical size={16} color={localAlignY === align ? '#3b82f6' : '#64748b'} />}
                    {align === 'middle' && <AlignCenterVertical size={16} color={localAlignY === align ? '#3b82f6' : '#64748b'} />}
                    {align === 'bottom' && <AlignEndVertical size={16} color={localAlignY === align ? '#3b82f6' : '#64748b'} />}
                  </button>
                ))}
              </div>
            </div>

            {/* 바닥 크기 조절 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Move size={14} />
                바닥 크기
              </label>

              {/* 가로 크기 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', width: '32px' }}>가로</span>
                <button
                  onClick={() => handleFloorWidthChange(-FLOOR_SIZE_STEP)}
                  disabled={selectedObject.width <= FLOOR_MIN_WIDTH}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: selectedObject.width <= FLOOR_MIN_WIDTH ? '#f1f5f9' : 'white',
                    cursor: selectedObject.width <= FLOOR_MIN_WIDTH ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={14} color={selectedObject.width <= FLOOR_MIN_WIDTH ? '#cbd5e1' : '#64748b'} />
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1e293b',
                  }}
                >
                  {selectedObject.width}px
                </span>
                <button
                  onClick={() => handleFloorWidthChange(FLOOR_SIZE_STEP)}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={14} color="#64748b" />
                </button>
              </div>

              {/* 세로 크기 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', width: '32px' }}>세로</span>
                <button
                  onClick={() => handleFloorHeightChange(-FLOOR_SIZE_STEP)}
                  disabled={selectedObject.height <= FLOOR_MIN_HEIGHT}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: selectedObject.height <= FLOOR_MIN_HEIGHT ? '#f1f5f9' : 'white',
                    cursor: selectedObject.height <= FLOOR_MIN_HEIGHT ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={14} color={selectedObject.height <= FLOOR_MIN_HEIGHT ? '#cbd5e1' : '#64748b'} />
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1e293b',
                  }}
                >
                  {selectedObject.height}px
                </span>
                <button
                  onClick={() => handleFloorHeightChange(FLOOR_SIZE_STEP)}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={14} color="#64748b" />
                </button>
              </div>

              <div
                style={{
                  marginTop: '8px',
                  fontSize: '11px',
                  color: '#94a3b8',
                }}
              >
                최소: {FLOOR_MIN_WIDTH}x{FLOOR_MIN_HEIGHT}px
              </div>
            </div>
          </>
        )}

        {/* V55.22: TextBox 속성 */}
        {type === 'textBox' && (
          <>
            {/* 텍스트 입력 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Type size={14} />
                텍스트
              </label>
              <textarea
                value={localText}
                onChange={(e) => setLocalText(e.target.value)}
                onBlur={handleBlur}
                placeholder="텍스트를 입력하세요..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {/* 폰트 크기 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Tag size={14} />
                글꼴 크기
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => handleFontSizeChange(-FONT_SIZE_STEP)}
                  disabled={localFontSize <= FONT_SIZE_MIN}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: localFontSize <= FONT_SIZE_MIN ? '#f1f5f9' : 'white',
                    cursor: localFontSize <= FONT_SIZE_MIN ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={14} color={localFontSize <= FONT_SIZE_MIN ? '#cbd5e1' : '#64748b'} />
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1e293b',
                  }}
                >
                  {localFontSize}px
                </span>
                <button
                  onClick={() => handleFontSizeChange(FONT_SIZE_STEP)}
                  disabled={localFontSize >= FONT_SIZE_MAX}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: localFontSize >= FONT_SIZE_MAX ? '#f1f5f9' : 'white',
                    cursor: localFontSize >= FONT_SIZE_MAX ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={14} color={localFontSize >= FONT_SIZE_MAX ? '#cbd5e1' : '#64748b'} />
                </button>
              </div>
            </div>

            {/* 텍스트 색상 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                글꼴 색상
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {TEXT_COLOR_PALETTE.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => handleTextColorChange(color.value)}
                    title={color.label}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      border: localTextColor === color.value ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                      background: color.value,
                      cursor: 'pointer',
                      boxShadow: localTextColor === color.value ? '0 0 0 2px rgba(59, 130, 246, 0.3)' : 'none',
                    }}
                  />
                ))}
              </div>
            </div>

            {/* 텍스트 박스 크기 조절 */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '8px',
                }}
              >
                <Move size={14} />
                크기
              </label>

              {/* 가로 크기 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', width: '32px' }}>가로</span>
                <button
                  onClick={() => handleTextBoxWidthChange(-TEXTBOX_SIZE_STEP)}
                  disabled={selectedObject.width <= TEXTBOX_MIN_WIDTH}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: selectedObject.width <= TEXTBOX_MIN_WIDTH ? '#f1f5f9' : 'white',
                    cursor: selectedObject.width <= TEXTBOX_MIN_WIDTH ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={14} color={selectedObject.width <= TEXTBOX_MIN_WIDTH ? '#cbd5e1' : '#64748b'} />
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1e293b',
                  }}
                >
                  {selectedObject.width}px
                </span>
                <button
                  onClick={() => handleTextBoxWidthChange(TEXTBOX_SIZE_STEP)}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={14} color="#64748b" />
                </button>
              </div>

              {/* 세로 크기 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', width: '32px' }}>세로</span>
                <button
                  onClick={() => handleTextBoxHeightChange(-TEXTBOX_SIZE_STEP)}
                  disabled={selectedObject.height <= TEXTBOX_MIN_HEIGHT}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: selectedObject.height <= TEXTBOX_MIN_HEIGHT ? '#f1f5f9' : 'white',
                    cursor: selectedObject.height <= TEXTBOX_MIN_HEIGHT ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={14} color={selectedObject.height <= TEXTBOX_MIN_HEIGHT ? '#cbd5e1' : '#64748b'} />
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1e293b',
                  }}
                >
                  {selectedObject.height}px
                </span>
                <button
                  onClick={() => handleTextBoxHeightChange(TEXTBOX_SIZE_STEP)}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={14} color="#64748b" />
                </button>
              </div>

              <div
                style={{
                  marginTop: '8px',
                  fontSize: '11px',
                  color: '#94a3b8',
                }}
              >
                최소: {TEXTBOX_MIN_WIDTH}x{TEXTBOX_MIN_HEIGHT}px
              </div>
            </div>
          </>
        )}
      </div>

      {/* 안내 */}
      <div
        style={{
          marginTop: 'auto',
          padding: '16px',
          background: '#f8fafc',
          borderTop: '1px solid var(--color-gray-200)',
          fontSize: '11px',
          color: '#64748b',
          lineHeight: '1.5',
        }}
      >
        {type === 'zone' && (
          <>
            <strong>Tip:</strong> 구역 코드는 좌석 표기에 사용됩니다.
            좌석을 이 구역에 연결하면 &quot;A-1&quot; 형식으로 표시됩니다.
          </>
        )}
        {type === 'seat' && (
          <>
            <strong>Tip:</strong> 좌석 번호는 수동 입력입니다.
            삭제/이동 시에도 번호가 유지됩니다.
          </>
        )}
        {type === 'meeting_room' && (
          <>
            <strong>Tip:</strong> 회의실 이름은 캔버스에 중앙 정렬로 표시됩니다.
            +/- 버튼으로 크기를 조절하세요.
          </>
        )}
        {type === 'floor' && (
          <>
            <strong>Tip:</strong> 바닥은 다른 오브젝트 아래에 배치됩니다.
            텍스트 정렬을 조절하여 원하는 위치에 표시하세요.
          </>
        )}
        {type === 'textBox' && (
          <>
            <strong>Tip:</strong> 텍스트 박스는 자유롭게 배치할 수 있습니다.
            색상 팔레트에서 원하는 글꼴 색상을 선택하세요.
          </>
        )}
      </div>
    </div>
  );
}
